import React, {useEffect, useCallback, useState} from "react";
import {isEmpty} from "lodash";
import PropTypes from "prop-types";
import {Col, Row, Button} from "reactstrap";
import {LuRefreshCcw} from "react-icons/lu";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useParams} from "react-router-dom";
import Cells from "../../components/Charts/Containers/Cell/Cells";
import SafeView from "../../components/safeView/SafeView";
import EmptyGraphMessage from "../../components/Charts/Containers/EmptyGraphMessage";
import RangePicker from "../../components/RangePicker";
import SelectCreator from "../../components/Forms/components/SelectCreator";
import {useCleanupOnUnload} from "../../hooks/useCleanupOnUnload";
import {useDisclosure} from "../../hooks/useDisclosure";
import {
  requestDashboardCellsData,
  storeDashboardCellsData,
} from "../../redux/Actions/tsdb.actions";
import {
  requestDataExplorerTimezones,
  storeDataExplorerTimezones,
} from "../../redux/Actions/data-explorer.actions";
import {getDashboardCellsData} from "../../redux/Selectors/tsdb.selector";
import {getDataExplorerTimezones} from "../../redux/Selectors/data-explorer.selectors";
import {getSelectedController} from "../../redux/Selectors/controllers.selectors";
import DashboardHeader from "./DashboardHeader";
import {useHandleTimeRange} from "./useHandleTimeRange";
import "./DashboardPage.scss";

function DashboardPage(props) {
  const {dashboard: dashboardProp = {}, isDraggable = true, isResizable = true} = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const {dashboardId} = useParams();
  const timezones = useSelector(getDataExplorerTimezones);
  const selectedController = useSelector(getSelectedController);
  const selectedDashboard = useSelector(getDashboardCellsData);
  const dashboard = isEmpty(dashboardProp) ? selectedDashboard : dashboardProp;

  const [refreshCount, setRefreshCount] = useState(0);
  const [timezone, setTimezone] = useState("Local");
  const {
    isOpen: isRangePickerOpen,
    onOpen: openRangePicker,
    onClose: closeRangePicker,
  } = useDisclosure();
  const {
    timeRangeOptions,
    timeRangeStart,
    timeRangeStop,
    windowPeriod,
    handleTimeRangeChange,
    handleTimeRangeSave,
  } = useHandleTimeRange(openRangePicker);

  const checkIsResizable = () => {
    if (location?.state?.isEditable !== undefined) {
      return location?.state?.isEditable;
    }

    return isResizable;
  };

  const checkIsDraggable = () => {
    if (location?.state?.isEditable !== undefined) {
      return location?.state?.isEditable;
    }

    return isDraggable;
  };

  const cleanup = useCallback(() => {
    dispatch(storeDashboardCellsData({}));
    dispatch(storeDataExplorerTimezones([]));
    setTimezone("Local");
  }, [dispatch]);

  useCleanupOnUnload(cleanup);

  const isEmptyDashboard = isEmpty(dashboardProp);
  const isControllerPage = location.pathname.split("/").includes("appliance");

  useEffect(() => {
    if (dashboardId && isEmptyDashboard) {
      dispatch(requestDashboardCellsData(dashboardId));
    }

    const payload = {};
    if (selectedController?.mac && isControllerPage) {
      payload.mac = selectedController.mac;
    }
    const callback = (options) => {
      setTimezone(options[0]?.value);
    };
    dispatch(requestDataExplorerTimezones(payload, callback));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SafeView>
      {isRangePickerOpen && (
        <RangePicker
          open={isRangePickerOpen}
          onCloseHandler={closeRangePicker}
          handleSave={handleTimeRangeSave}
          timeZone={timezone}
        />
      )}
      <div className="dashboard-page-container">
        {dashboard.name && <DashboardHeader name={dashboard.name} />}
        <Row className="mt-1">
          <Col md="5" xl="3">
            <Row>
              <SelectCreator
                id="timezone"
                classNamePrefix={"timezone"}
                colSize={12}
                placeholder={"Choose a timezone"}
                name="timezone"
                options={timezones}
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                isSearchable
              />
            </Row>
          </Col>
          <Col md={1} className="mt-2 mb-2 mt-md-0 mb-md-0 d-flex justify-content-center">
            <Button
              className="d-flex align-items-center"
              style={{maxHeight: "38px"}}
              onClick={() => {
                setRefreshCount((prevCount) => prevCount + 1);
              }}
              data-cy={"dashboard-refresh"}
            >
              <LuRefreshCcw />
            </Button>
          </Col>
          <Col md="6" xl="4">
            <Row>
              <SelectCreator
                id="timeRangeStart"
                classNamePrefix={"timeRangeStart"}
                colSize={12}
                placeholder={"Look-back period"}
                name="timeRangeStart"
                options={timeRangeOptions}
                value={timeRangeStart}
                onChange={handleTimeRangeChange}
                isSearchable
                hideSelectedOptions
              />
            </Row>
          </Col>
        </Row>

        {dashboard.cells && dashboard.cells.length > 0 ? (
          <Cells
            dashboardId={dashboard.id}
            cells={dashboard.cells}
            isResizable={checkIsResizable()}
            isDraggable={checkIsDraggable()}
            timeRangeStart={timeRangeStart}
            timeRangeStop={timeRangeStop}
            windowPeriod={windowPeriod}
            timeZone={timezone}
            refreshCount={refreshCount}
          />
        ) : (
          <div style={{height: "500px"}}>
            <EmptyGraphMessage message="This Dashboard doesn't have any cells" />
          </div>
        )}
      </div>
    </SafeView>
  );
}

DashboardPage.propTypes = {
  dashboardId: PropTypes.string,
  isResizable: PropTypes.bool,
  isDraggable: PropTypes.bool,
  dashboard: PropTypes.object,
};

export default DashboardPage;
